import React, { useEffect, useState } from 'react';
import { Button, Text, View } from "../../../src/Components/Themed"
import LeagueSchedule from '../../market2/components/LeagueSchedule';
import type { AthleteProps, BestAvailableOrderProps, EventOrderStatProps, EventProps, ExternalPriceProps, LeagueProps, MarketProps, OrderProps, PollProps, TradeProps } from '../../../src/types';
import { useColors } from '../../../src/constants/useColors';
import { MarketComponentApi } from '../../../src/MarketComponents/api';
import { MarketMakerApi } from '../../../src/MarketMaker/api';
import TeamEventMarket from '../../../src/MarketComponents/components/TeamEventMarket';
import { ActivityIndicator, FlatList, Image } from 'react-native';
import { Icons } from '../../../src/Components';
import { MarketAppHelpers } from '../helpers';
import { MarketComponents } from 'be-components';
import DropDown from '../../../src/Components/Dropdown';
import SearchBox from '../../../src/Components/SearchBox';

type DesktopMarketProps = {
    league?:LeagueProps,
    markets:MarketProps[],
    onFlashSelect:(poll_id:string) => void,
    init_player_market?:MarketProps,
    flash_markets:PollProps[],
    init_event?:EventProps,
    event_order_stats: { [key:string]:EventOrderStatProps[] },
    best_available_orders:{ [key:string]: BestAvailableOrderProps[] },
    onOrder:(order:OrderProps) => void
}

const DesktopMarket = ({ league, init_event, init_player_market, best_available_orders, flash_markets, markets, event_order_stats, onFlashSelect, onOrder }:DesktopMarketProps) => {
    const Colors = useColors();
    const [ search_players, setSearchPlayers ] = useState({
        active:false,
        value:''
    });
    const [ market_data, setMarketData ] = useState<{
        loading:boolean,
        external_prices:ExternalPriceProps[],
        athletes:AthleteProps[],
        latest_trades:TradeProps[],
        active_event?:EventProps,
        active_player_market?:MarketProps
    }>({
        loading:false,
        athletes:[],
        external_prices: [],
        latest_trades: [],
    });
    const { loading, active_event, latest_trades, athletes, external_prices, active_player_market } = market_data;
    const ev_available_orders = best_available_orders[`team:${active_event?.event_id}`] ?? []
    const ev_flashes = flash_markets.filter(p => p.contest_type == 'team' && p.contest_id == active_event?.event_id);

    //Snag the player markets
    let player_markets = markets.filter(m => active_event?.supported_markets?.map(sm => sm.market_id.toString()).includes(m.market_id.toString()) && m.level == 'athlete')
    let props = active_event ? MarketAppHelpers.getPropTradesForEvent(player_markets, latest_trades, active_event.event_id) : []
    let active_player_props = props.filter(p => p.market_id == active_player_market?.market_id);
    if(search_players.active){
        active_player_props = active_player_props.filter(pp => {
            let athlete = athletes.find(a => a.athlete_id == pp.side_id);
            if(!athlete){ return }
            if(`${athlete.first_name} ${athlete.last_name}`.toLowerCase().includes(search_players.value.toLowerCase())){ return pp }
            return
        })
    }
    useEffect(() => {
        if(!init_event){ return }
        onSelectEvent(init_event, init_player_market)
    },[init_event, init_player_market])


    const onSelectEvent = async(ev:EventProps, init_player_market?:MarketProps) => {
        MarketMakerApi.setEnvironment();
        setMarketData({ ...market_data, loading: true });
        const evs = await MarketComponentApi.getEventsByEventIds([ev.event_id]);
        const new_event = evs[0];
        const prices = await MarketMakerApi.getPricesByEvents('team', [ev.event_id]);
        const tds = await MarketComponentApi.getLatestTradesByEvents('team', [ev.event_id]);
        //Lets search for the best player prop market
        let pms = markets.filter(m => ev?.supported_markets?.map(sm => sm.market_id.toString()).includes(m.market_id.toString()) && m.level == 'athlete')
        let props =  MarketAppHelpers.getPropTradesForEvent(pms, tds, ev.event_id);
        let active_pm = pms.find(m => m.market_id == props[0]?.market_id);
        let unique_ids = [ ...new Set(tds.filter(p => p.side_type == 'athlete').map(p => p.side_id)) ]
        let needed_ids = unique_ids.filter(id => !athletes.map(a => a.athlete_id.toString()).includes(id.toString()));
        const aths = await MarketComponentApi.getAthletesByIds(needed_ids);
        setMarketData({ 
            ...market_data,
            active_event: new_event,
            external_prices: prices,
            latest_trades: tds,
            active_player_market: active_player_market ?? init_player_market ?? active_pm,
            athletes: athletes.filter(a => !aths.find(na => na.athlete_id == a.athlete_id)).concat(aths),
            loading: false
        });
    }

    const renderAthleteProps = (data:{ item:TradeProps, index:number, compact?:boolean }) => {
        if(!active_event){ return <></> }
        const market = markets.find(m => m.market_id == data.item.market_id);
        if(!market){ return <></> }
        const athlete = athletes.find(a => a.athlete_id == data.item.side_id);
        if(!athlete){ return <></> }
        let best_available = best_available_orders[`team:${data.item.event_id}`] ?? []
        best_available = best_available.filter(a => a.market_id == data.item.market_id && a.side_type == 'athlete' && a.side_id == data.item.side_id);
        let event_trades = latest_trades.filter(t => t.event_id == active_event.event_id);
        event_trades = event_trades.filter(a => a.market_id == data.item.market_id && a.side_type == 'athlete' && a.side_id == data.item.side_id);
        let order_stats = event_order_stats[`team:${data.item.event_id}`] ?? []
        order_stats = order_stats.filter(a => a.market_id == data.item.market_id && a.side_type == 'athlete' && a.side_id == data.item.side_id);
        return (
                <View key={data.item.trade_id.toString()} style={{ paddingLeft:5, paddingRight:5, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                    <MarketComponents.AthleteMarket
                        event={active_event}
                        direction='horizontal'
                        image_size={50}
                        style={{ padding:0 }}
                        hide_stat_banner
                        best_available_orders={best_available}
                        event_order_stats={order_stats}
                        latest_trades={event_trades}
                        onOrder={onOrder}
                        event_type='team'
                        onTradeLongPress={() => console.log('')}
                        onView={(data) => console.log(data)}
                        hide_liquidity
                        market={market}
                        default_price_view='best_available'
                        athlete={athlete}
                    />
                </View>
                )   
    }

    return (
        <View style={{ flex:1 }}>
            <View style={{ flex:1, flexDirection:'row' }}>
                <View style={{ flex:1, borderWidth:1, borderColor:Colors.borders.light }}>
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:12 }}>
                        <Image
                            source={{ uri:league?.league_image }}
                            style={{ height:30, width:30 }}
                            resizeMode='contain'
                        />
                        <View transparent style={{ flex:1, marginLeft:10 }}>
                            <Text theme='h2'>League Schedule</Text>
                            <Text theme='action' style={{ marginTop:3 }}>Select a contest for details</Text>
                        </View>
                    </View>
                    {league ?
                    <LeagueSchedule
                        league={league}
                        active_event={active_event?.event_id}
                        onSelectEvent={(e) => onSelectEvent(e)}
                    />
                    :<></>}
                </View>
                <View style={{ flex:2,  borderWidth:1, borderColor:Colors.borders.light }}>
                       <View>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:12 }}>
                                <Image
                                    source={{ uri:league?.league_image }}
                                    style={{ height:30, width:30 }}
                                    resizeMode='contain'
                                />
                                <View transparent style={{ flex:1, marginLeft:10 }}>
                                    <Text theme='h2'>Primary Markets</Text>
                                    <Text theme='action' style={{ marginTop:3 }}>Below are the primary markets for this contest</Text>
                                </View>
                            </View>
                            <View style={{ padding:10 }}>
                                {active_event ?
                                <TeamEventMarket
                                    event={active_event}
                                    latest_trades={latest_trades}
                                    best_available_orders={ev_available_orders}
                                    onOrder={onOrder}
                                    float                    
                                    hide_liquidity
                                    hide_non_primary
                                    markets={markets}
                                    flash_markets={ev_flashes}
                                    onFlashSelect={onFlashSelect}
                                    event_order_stats={[]}
                                    default_price_view='best_available'
                                    onView={() => console.log('')}
                                    onCompetitionSelect={() => console.log('')}
                                    onSquaresSelect={() => console.log('')}
                                    onTradeLongPress={() => console.log('')}
                                    onViewAdditionalMarkets={() => console.log('')}
                                />
                                : loading ?
                                <ActivityIndicator
                                    size={50}
                                    color={Colors.text.h1}
                                    style={{ padding:20, alignSelf:'center' }}
                                />
                                :
                                <Text theme='h1' style={{ alignSelf:'center' }} size={32}>SELECT AN EVENT</Text>
                                }
                            </View>
                       </View>
                       
                       
                </View>
                <View style={{ flex:2 }}>
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                        <Icons.AthleteIcon size={26} color={Colors.text.action}/>
                        <View transparent style={{ flex:1, marginLeft:10 }}>
                            <Text theme='h2'>Player Markets</Text>
                            <Text theme='action' style={{ marginTop:3 }}>Select dropdown to change market</Text>
                        </View>
                        <Button float style={{ padding:10 }}
                            onPress={() => setSearchPlayers({ active:!search_players.active, value:'' })}
                        >
                            {search_players.active ?
                            <Icons.CloseIcon color={Colors.text.error} size={14} />
                            :
                            <Icons.SearchIcon color={Colors.text.h1} size={14} />
                            }
                        </Button>
                        <DropDown
                            dropdown_options={[
                                { value: 'market', eligible_options: player_markets.map(pm => pm.stat_label ?? pm.stat) }
                            ]}
                            
                            onOptionSelect={(stat:string) => {
                                let selected = player_markets.find(m => m.stat_label == stat);
                                if(!selected){ selected = player_markets.find(m => m.stat == stat) }
                                if(!selected){ return }
                                setMarketData({ ...market_data, active_player_market: selected })
                            }}
                            selected_value={active_player_market?.stat_label ?? active_player_market?.stat ?? ''}
                        />
                    </View>
                    {search_players.active ? 
                    <SearchBox
                        hide_search_button
                        onChange={(text) => setSearchPlayers({ ...search_players, value: text })}
                    />
                    :<></>}
                    <View style={{ flex:1 }}>
                        <FlatList
                            data={active_player_props}
                            key={'event_player_props'}
                            keyExtractor={(item) => item.trade_id}
                            renderItem={renderAthleteProps}
                        />
                    </View>
                </View>
            </View>
        </View>
    )
}

export default DesktopMarket